import { type Component, Show } from "solid-js";
import {
  phase,
  prompt,
  setPrompt,
  errorMsg,
  loadProgress,
  downloadProgress,
  downloadLabel,
  modelsFromCache,
} from "../lib/store.ts";

export interface StartOverlayProps {
  onDownload: () => void;
  onLoadModel: () => void;
  onLoadMmproj: () => void;
  onStart: () => void;
  modelName: () => string;
  mmprojName: () => string;
}

const StartOverlay: Component<StartOverlayProps> = (props) => {
  const visible = () => phase() === "startup" || phase() === "loading-model";
  const loading = () => phase() === "loading-model";
  const downloading = () => downloadProgress() >= 0;
  const busy = () => loading() || downloading();
  const hasModel = () => props.modelName().length > 0;
  const hasMmproj = () => props.mmprojName().length > 0;
  const canStart = () => hasModel() && hasMmproj() && !busy();

  const dlPercent = () => {
    const p = downloadProgress();
    return p >= 0 ? Math.round(p * 100) : 0;
  };

  return (
    <div class="overlay" classList={{ hidden: !visible() }}>
      <h1>MiDashengLM</h1>
      <p class="subtitle">real-time audio captioning — in-browser inference</p>

      {/* Prompt input */}
      <div class="prompt-group">
        <span class="prompt-label">prompt</span>
        <input
          class="prompt-input"
          type="text"
          value={prompt()}
          spellcheck={false}
          autocomplete="off"
          placeholder="Enter your prompt here..."
          onInput={(e) => setPrompt(e.currentTarget.value)}
        />
      </div>

      {/* Model selection */}
      <div class="model-group">
        {/* Cache status */}
        <Show when={modelsFromCache()}>
          <p class="cache-status">{"\u2713"} Models loaded from cache</p>
        </Show>

        {/* Download button */}
        <Show when={!hasModel() || !hasMmproj()}>
          <button
            class="download-btn"
            onClick={props.onDownload}
            disabled={busy()}
          >
            Download default models
          </button>
        </Show>

        {/* Download progress */}
        <Show when={downloading()}>
          <div class="loading-bar-container">
            <div
              class="loading-bar"
              style={{ width: `${dlPercent()}%` }}
            />
            <span class="loading-label">{downloadLabel()}</span>
          </div>
        </Show>

        {/* Separator */}
        <Show when={!hasModel() || !hasMmproj()}>
          <div class="or-separator">
            <span>or select local files</span>
          </div>
        </Show>

        {/* Local file pickers */}
        <div class="model-row">
          <button
            class="model-btn"
            onClick={props.onLoadModel}
            disabled={busy()}
          >
            {hasModel()
              ? `\u2713 ${props.modelName()}`
              : "Select backbone (.gguf)"}
          </button>
        </div>
        <div class="model-row">
          <button
            class="model-btn"
            onClick={props.onLoadMmproj}
            disabled={busy()}
          >
            {hasMmproj()
              ? `\u2713 ${props.mmprojName()}`
              : "Select mmproj (.gguf)"}
          </button>
        </div>
      </div>

      {/* Model loading progress */}
      <Show when={loading() && loadProgress() >= 0}>
        <div class="loading-bar-container">
          <div
            class="loading-bar"
            style={{ width: `${loadProgress() * 100}%` }}
          />
          <span class="loading-label">Loading model...</span>
        </div>
      </Show>

      <Show when={loading() && loadProgress() < 0}>
        <p class="loading-label">Loading model...</p>
      </Show>

      <button
        class="start-btn"
        onClick={props.onStart}
        disabled={!canStart()}
      >
        Start Listening
      </button>

      <Show when={errorMsg()}>
        <p class="error-msg">{errorMsg()}</p>
      </Show>
    </div>
  );
};

export default StartOverlay;
